/**
 * Consolidated Error Handling Utilities
 * Combines functionality from errorHandling.js, enhancedErrorHandling.js, and errorReporting.js
 * Provides centralized error handling, user-friendly messages, retry mechanisms, and reporting
 */

import debugLogger from './debugLogger.js';

// Create a dedicated ApiClient instance for error reporting
// Use dynamic import to avoid circular dependency
let errorReportingClient = null;

const getErrorReportingClient = async () => {
  if (!errorReportingClient) {
    // Create a simple HTTP client for error reporting to avoid circular dependency
    errorReportingClient = {
      post: async (endpoint, data) => {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}${endpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          return await response.json();
        } catch (error) {
          console.error('[ERROR-REPORTING] Failed to report error:', error);
          // Don't throw - we don't want error reporting to cause more errors
          return null;
        }
      }
    };
  }
  return errorReportingClient;
};

/**
 * Error categories for better error classification
 */
export const ERROR_CATEGORIES = {
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  VALIDATION: 'validation',
  EXTERNAL_SERVICE: 'external_service',
  SYSTEM: 'system',
  RATE_LIMIT: 'rate_limit',
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Standardized error response format
 */
export class StandardizedError extends Error {
  constructor({
    message,
    code = 'UNKNOWN_ERROR',
    category = ERROR_CATEGORIES.SYSTEM,
    severity = ERROR_SEVERITY.MEDIUM,
    status = 500,
    canRetry = false,
    retryAfter = null,
    fallbackAvailable = false,
    userMessage = null,
    technicalDetails = null,
    actionButtons = [],
    originalError = null
  }) {
    super(message);
    this.name = 'StandardizedError';
    this.code = code;
    this.category = category;
    this.severity = severity;
    this.status = status;
    this.canRetry = canRetry;
    this.retryAfter = retryAfter;
    this.fallbackAvailable = fallbackAvailable;
    this.userMessage = userMessage || this.generateUserMessage();
    this.technicalDetails = technicalDetails;
    this.actionButtons = actionButtons;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }

  generateUserMessage() {
    switch (this.category) {
      case ERROR_CATEGORIES.NETWORK:
        return 'Connection issue. Please check your internet connection and try again.';
      case ERROR_CATEGORIES.AUTHENTICATION:
        return 'Please sign in again to continue.';
      case ERROR_CATEGORIES.AUTHORIZATION:
        return 'You don\'t have permission to perform this action.';
      case ERROR_CATEGORIES.VALIDATION:
        return 'Please check your input and try again.';
      case ERROR_CATEGORIES.RATE_LIMIT:
        return 'Too many requests. Please wait a moment and try again.';
      case ERROR_CATEGORIES.NOT_FOUND:
        return 'The requested resource was not found.';
      case ERROR_CATEGORIES.EXTERNAL_SERVICE:
        return 'External service is temporarily unavailable. Please try again later.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }
}

/**
 * Fetch with retry logic and enhanced error handling
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<Response>} Fetch response
 */
export const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || 30000);
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Don't retry on client errors (4xx) except for specific cases
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        return response;
      }
      
      // Retry on server errors (5xx) and rate limiting (429)
      if (response.status >= 500 || response.status === 429) {
        if (attempt === maxRetries) {
          return response;
        }
        
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry on abort errors
      if (error.name === 'AbortError') {
        throw error;
      }
      
      // Retry on network errors
      if (attempt < maxRetries && isRetryableError(error)) {
        const delay = getRetryDelay(attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      throw error;
    }
  }
  
  throw lastError;
};

/**
 * Check if an error is retryable
 * @param {Error} error - Error to check
 * @returns {boolean} Whether the error is retryable
 */
export const isRetryableError = (error) => {
  if (!error) return false;
  
  // Network errors are retryable
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return true;
  }
  
  // Timeout errors are retryable
  if (error.name === 'AbortError') {
    return false; // Don't retry abort errors
  }
  
  // Check for specific error codes
  if (error.status) {
    return error.status >= 500 || error.status === 429;
  }
  
  return false;
};

/**
 * Get retry delay with exponential backoff
 * @param {number} attempt - Current attempt number
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @returns {number} Delay in milliseconds
 */
export const getRetryDelay = (attempt, baseDelay = 1000, maxDelay = 10000) => {
  const delay = baseDelay * Math.pow(2, attempt);
  return Math.min(delay, maxDelay);
};

/**
 * Parse API error response into standardized format
 * @param {Response} response - Fetch response
 * @returns {Promise<StandardizedError>} Standardized error
 */
export const parseApiError = async (response) => {
  let errorData = {};
  
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    } else {
      errorData = { message: await response.text() };
    }
  } catch (parseError) {
    errorData = { message: `HTTP ${response.status} ${response.statusText}` };
  }
  
  return new StandardizedError({
    message: errorData.message || `HTTP ${response.status} ${response.statusText}`,
    code: errorData.code || `HTTP_${response.status}`,
    category: getCategoryFromStatus(response.status),
    severity: getSeverityFromStatus(response.status),
    status: response.status,
    canRetry: isRetryableStatus(response.status),
    technicalDetails: errorData,
    originalError: errorData
  });
};

/**
 * Parse network error into standardized format
 * @param {Error} error - Network error
 * @returns {StandardizedError} Standardized error
 */
export const parseNetworkError = (error) => {
  return new StandardizedError({
    message: error.message || 'Network error occurred',
    code: 'NETWORK_ERROR',
    category: ERROR_CATEGORIES.NETWORK,
    severity: ERROR_SEVERITY.MEDIUM,
    canRetry: true,
    originalError: error
  });
};

/**
 * Get error category from HTTP status code
 * @param {number} status - HTTP status code
 * @returns {string} Error category
 */
const getCategoryFromStatus = (status) => {
  if (status === 401) return ERROR_CATEGORIES.AUTHENTICATION;
  if (status === 403) return ERROR_CATEGORIES.AUTHORIZATION;
  if (status === 404) return ERROR_CATEGORIES.NOT_FOUND;
  if (status === 409) return ERROR_CATEGORIES.CONFLICT;
  if (status === 422) return ERROR_CATEGORIES.VALIDATION;
  if (status === 429) return ERROR_CATEGORIES.RATE_LIMIT;
  if (status >= 500) return ERROR_CATEGORIES.EXTERNAL_SERVICE;
  return ERROR_CATEGORIES.SYSTEM;
};

/**
 * Get error severity from HTTP status code
 * @param {number} status - HTTP status code
 * @returns {string} Error severity
 */
const getSeverityFromStatus = (status) => {
  if (status === 401 || status === 403) return ERROR_SEVERITY.HIGH;
  if (status === 404 || status === 422) return ERROR_SEVERITY.MEDIUM;
  if (status === 429) return ERROR_SEVERITY.LOW;
  if (status >= 500) return ERROR_SEVERITY.HIGH;
  return ERROR_SEVERITY.MEDIUM;
};

/**
 * Check if HTTP status is retryable
 * @param {number} status - HTTP status code
 * @returns {boolean} Whether the status is retryable
 */
const isRetryableStatus = (status) => {
  return status >= 500 || status === 429;
};

/**
 * Error Reporting Class
 * Handles error reporting and tracking
 */
class ErrorReporting {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.errorQueue = [];
    this.maxQueueSize = 100;
    this.reportingEndpoint = '/api/errors/report';
    this.batchSize = 10;
    this.flushInterval = 30000; // 30 seconds
    
    // Error code generation
    this.errorCodePrefix = 'PM'; // ParrotMate
    this.componentCodes = {
      'ParrotWizardStep1': 'PWS1',
      'ApiClient': 'API',
      'PerformanceMonitor': 'PERF',
      'CacheManager': 'CACHE',
      'Authentication': 'AUTH',
      'General': 'GEN'
    };
    
    // Initialize error reporting
    this.initializeErrorReporting();
    
    debugLogger.info('ErrorReporting initialized', {
      isProduction: this.isProduction,
      reportingEndpoint: this.reportingEndpoint,
      batchSize: this.batchSize
    });
  }

  /**
   * Initialize error reporting with global error handlers
   */
  initializeErrorReporting() {
    // Global error handler for unhandled errors
    window.addEventListener('error', (event) => {
      this.reportError(event.error || new Error(event.message), {
        type: 'unhandled_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        source: 'global_error_handler'
      });
    });

    // Global promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError(event.reason, {
        type: 'unhandled_promise_rejection',
        source: 'global_promise_handler'
      });
    });

    // Start periodic error queue flushing
    if (this.isProduction) {
      setInterval(() => this.flushErrorQueue(), this.flushInterval);
    }
  }

  /**
   * Report an error
   * @param {Error|StandardizedError} error - Error to report
   * @param {object} context - Additional context
   */
  reportError(error, context = {}) {
    const errorReport = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: error.code,
        category: error.category,
        severity: error.severity
      },
      context: {
        ...context,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
    };

    // Add to queue
    this.errorQueue.push(errorReport);

    // Trim queue if too large
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue = this.errorQueue.slice(-this.maxQueueSize);
    }

    // Log in development
    if (this.isDevelopment) {
      debugLogger.error('Error reported:', errorReport);
    }

    // Flush immediately for critical errors
    if (error.severity === ERROR_SEVERITY.CRITICAL) {
      this.flushErrorQueue();
    }
  }

  /**
   * Generate unique error ID
   * @returns {string} Error ID
   */
  generateErrorId() {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `${this.errorCodePrefix}_${timestamp}_${random}`;
  }

  /**
   * Flush error queue to reporting endpoint
   */
  async flushErrorQueue() {
    if (this.errorQueue.length === 0) return;

    const batch = this.errorQueue.splice(0, this.batchSize);
    
    try {
      await getErrorReportingClient().post(this.reportingEndpoint, { errors: batch });
      
      debugLogger.info(`Flushed ${batch.length} errors to reporting endpoint`);
    } catch (flushError) {
      // Put errors back in queue if reporting fails
      this.errorQueue.unshift(...batch);
      debugLogger.error('Failed to flush error queue:', flushError);
    }
  }
}

// Create singleton instance
const errorReporting = new ErrorReporting();

/**
 * Report initialization error
 * @param {Error} error - Error to report
 * @param {Object} context - Additional context
 */
// Backward-compatible initialization error reporter that also returns
// an error code and friendly support message for UI consumption.
export const reportInitializationError = (error, ...args) => {
  let context = {};
  // Support both (error, context) and (error, operation, component, extra)
  if (args.length === 1 && typeof args[0] === 'object') {
    context = args[0] || {};
  } else if (args.length > 0) {
    const [operation, component, extra] = args;
    context = {
      operation,
      component,
      ...(extra || {})
    };
  }

  // Ensure standardized error shape when reporting
  const toReport = error instanceof StandardizedError
    ? error
    : new StandardizedError({ message: error?.message || 'Initialization error', originalError: error });

  errorReporting.reportError(toReport, { ...context, category: 'initialization' });

  // Generate a lightweight, user-facing reference code
  const code = `INIT_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,5)}`;
  const userFriendlyMessage = {
    title: 'Initialization issue',
    description: 'We had trouble loading this step.',
    supportMessage: `If this continues, please contact support and include code ${code}.`
  };

  return { errorCode: code, userFriendlyMessage };
};

/**
 * Report API error
 * @param {Error} error - Error to report
 * @param {Object} context - Additional context
 */
export const reportApiError = (error, context = {}) => {
  errorReporting.reportError(error, { ...context, category: 'api' });
};

/**
 * Standardize error into StandardizedError format
 * @param {Error|any} error - Error to standardize
 * @param {object} context - Additional context
 * @returns {StandardizedError} Standardized error
 */
export const standardizeError = (error, context = {}) => {
  if (error instanceof StandardizedError) {
    return error;
  }
  
  if (error instanceof Error) {
    return new StandardizedError({
      message: error.message,
      code: error.code || 'UNKNOWN_ERROR',
      category: context.category || ERROR_CATEGORIES.SYSTEM,
      severity: context.severity || ERROR_SEVERITY.MEDIUM,
      originalError: error,
      ...context
    });
  }
  
  return new StandardizedError({
    message: typeof error === 'string' ? error : 'Unknown error occurred',
    code: 'UNKNOWN_ERROR',
    category: context.category || ERROR_CATEGORIES.SYSTEM,
    severity: context.severity || ERROR_SEVERITY.MEDIUM,
    originalError: error,
    ...context
  });
};

/**
 * Create error context for enhanced error reporting
 * @param {object} context - Context information
 * @returns {object} Error context
 */
export const createErrorContext = (context = {}) => {
  return {
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...context
  };
};

/**
 * Log error with context
 * @param {Error} error - Error to log
 * @param {object} context - Additional context
 */
export const logError = (error, context = {}) => {
  errorReporting.reportError(error, context);
};

/**
 * Error recovery strategies
 */
export const ERROR_RECOVERY_STRATEGIES = {
  RETRY: 'retry',
  FALLBACK: 'fallback',
  REDIRECT: 'redirect',
  REFRESH: 'refresh',
  IGNORE: 'ignore'
};

/**
 * Get recovery strategies for an error
 * @param {StandardizedError} error - Error to get strategies for
 * @returns {string[]} Array of recovery strategies
 */
export const getRecoveryStrategies = (error) => {
  const strategies = [];
  
  if (error.canRetry) {
    strategies.push(ERROR_RECOVERY_STRATEGIES.RETRY);
  }
  
  if (error.fallbackAvailable) {
    strategies.push(ERROR_RECOVERY_STRATEGIES.FALLBACK);
  }
  
  switch (error.category) {
    case ERROR_CATEGORIES.AUTHENTICATION:
      strategies.push(ERROR_RECOVERY_STRATEGIES.REDIRECT);
      break;
    case ERROR_CATEGORIES.NETWORK:
      strategies.push(ERROR_RECOVERY_STRATEGIES.RETRY, ERROR_RECOVERY_STRATEGIES.REFRESH);
      break;
    case ERROR_CATEGORIES.VALIDATION:
      strategies.push(ERROR_RECOVERY_STRATEGIES.IGNORE);
      break;
    default:
      if (!strategies.length) {
        strategies.push(ERROR_RECOVERY_STRATEGIES.REFRESH);
      }
  }
  
  return strategies;
};

export { errorReporting };
export default {
  StandardizedError,
  fetchWithRetry,
  parseApiError,
  parseNetworkError,
  isRetryableError,
  reportInitializationError,
  reportApiError,
  getRetryDelay,
  errorReporting,
  ERROR_CATEGORIES,
  ERROR_SEVERITY,
  standardizeError,
  createErrorContext,
  logError,
  getRecoveryStrategies,
  ERROR_RECOVERY_STRATEGIES
};
